import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { fetchContact, fetchConversation } from '../api/endpoints.js';
import { PageHeader } from '../components/PageHeader.js';
import { ConversationPanel } from '../components/ConversationPanel.js';
import { ChatComposer } from '../components/ChatComposer.js';
import { ErrorState, Loading } from '../components/States.js';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function ConversationDetailPage() {
  const { id = '' } = useParams();

  const convQ = useQuery({
    queryKey: ['conversation', id],
    queryFn: () => fetchConversation(id),
    enabled: Boolean(id),
  });

  const contactId = convQ.data?.contact_id ?? null;
  const contactQ = useQuery({
    queryKey: ['contact', contactId],
    queryFn: () => fetchContact(contactId as string),
    enabled: Boolean(contactId),
  });

  if (convQ.isLoading) return <Loading />;
  if (convQ.isError) return <ErrorState error={convQ.error} onRetry={() => convQ.refetch()} />;

  const conv = convQ.data;
  const contact = contactQ.data;
  const contactName =
    contact && (contact.first_name || contact.last_name)
      ? [contact.first_name, contact.last_name].filter(Boolean).join(' ')
      : contact?.company || contact?.email || contact?.phone_e164 || null;

  return (
    <div className="page">
      <PageHeader
        title="Conversation"
        subtitle={conv ? `${conv.channel} · ${conv.direction} · ${conv.status}` : undefined}
        actions={
          <Link to="/conversations" className="btn btn--ghost">
            ← Back
          </Link>
        }
      />

      {conv?.contact_id && (
        <section className="card">
          <div className="card__title-row">
            <h2 className="card__title">Contact</h2>
            <Link to={`/contacts/${conv.contact_id}`} className="btn btn--ghost btn--small">
              View full profile →
            </Link>
          </div>
          {contactQ.isLoading ? (
            <p className="muted">Loading contact…</p>
          ) : contact ? (
            <>
              {contactName && <p className="contact-overview__name">{contactName}</p>}
              <dl className="kv kv--columns">
                <dt>Email</dt>
                <dd>{contact.email ?? '—'}</dd>
                <dt>Phone</dt>
                <dd>{contact.phone_e164 ?? '—'}</dd>
                <dt>Company</dt>
                <dd>{contact.company ?? '—'}</dd>
                <dt>Lifecycle</dt>
                <dd>{contact.lifecycle_status ?? '—'}</dd>
              </dl>
            </>
          ) : (
            <p className="muted">Contact details unavailable.</p>
          )}
        </section>
      )}

      {conv && (
        <section className="card">
          <dl className="kv kv--columns">
            <dt>Status</dt>
            <dd>
              <span className={`badge badge--status-${conv.status}`}>{conv.status}</span>
            </dd>
            <dt>Queue</dt>
            <dd>{conv.queue_name ?? '—'}</dd>
            <dt>Started</dt>
            <dd>{formatDate(conv.started_at)}</dd>
            <dt>Ended</dt>
            <dd>{formatDate(conv.ended_at)}</dd>
            <dt>Source domain</dt>
            <dd>{conv.source_domain ?? '—'}</dd>
            <dt>Source number</dt>
            <dd>{conv.source_phone_number_e164 ?? '—'}</dd>
          </dl>
        </section>
      )}

      {conv?.channel === 'chat' && conv?.status !== 'completed' && (
        <ChatComposer conversationId={id} contactId={conv?.connect_contact_id ?? null} />
      )}

      <ConversationPanel
        conversationId={id}
        currentDisposition={conv?.disposition}
        conversationStatus={conv?.status}
      />
    </div>
  );
}
